import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePortal } from '@/contexts/PortalContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Camera, FileText, MessageSquare, CreditCard, Construction, ChevronRight, Bell } from 'lucide-react';

export default function PortalDashboard() {
  const { api, user } = usePortal();
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { api('/dashboard').then(r => setData(r.data)).catch(() => {}); }, [api]);

  if (!data) return <div className="space-y-4">{[1,2].map(i => <div key={i} className="h-32 bg-white rounded-xl animate-pulse" />)}</div>;

  const proj = data.projects?.[0];

  return (
    <div className="space-y-6" data-testid="portal-dashboard">
      {/* Welcome */}
      <div>
        <h1 className="text-2xl font-extrabold text-[#1e3a50]">Welcome, {data.name?.split(' ')[0]}</h1>
        <p className="text-sm font-medium text-[#6b8a9e]">Your pool project at a glance</p>
      </div>

      {proj ? (
        <>
          {/* Project Card */}
          <Card className="border-0 shadow-[0_1px_4px_rgba(0,0,0,0.06)]">
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h2 className="text-lg font-extrabold text-[#1e3a50]">{proj.name}</h2>
                  <p className="text-xs font-medium text-[#6b8a9e]">{proj.city}</p>
                </div>
                <Badge className="bg-[#4a9ac5]/10 text-[#2a6496] text-xs font-bold">{proj.status}</Badge>
              </div>
              <Progress value={proj.progress_pct} className="h-3 rounded-full mb-3" />
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-[#f5f9fb] rounded-lg text-center">
                  <p className="text-xl font-extrabold font-mono text-[#2a6496]">{proj.progress_pct}%</p>
                  <p className="text-[9px] font-bold text-[#6b8a9e] uppercase">Complete</p>
                </div>
                <div className="p-3 bg-[#f5f9fb] rounded-lg text-center">
                  <p className="text-sm font-bold text-[#1e3a50]">{proj.current_stage || 'Starting'}</p>
                  <p className="text-[9px] font-bold text-[#6b8a9e] uppercase">Current Stage</p>
                </div>
                <div className="p-3 bg-[#f5f9fb] rounded-lg text-center">
                  <p className="text-sm font-bold text-[#1e3a50]">{proj.next_stage || '—'}</p>
                  <p className="text-[9px] font-bold text-[#6b8a9e] uppercase">Next Up</p>
                </div>
              </div>
              {proj.target_end_date && (
                <p className="text-xs font-medium text-[#6b8a9e] mt-3">Target completion: <span className="font-bold text-[#2a4a60]">{proj.target_end_date}</span></p>
              )}
            </CardContent>
          </Card>

          {/* Latest Update */}
          {proj.latest_update && (
            <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)] cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/portal/photos')}>
              <CardContent className="p-4 flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                  <Bell className="w-4 h-4 text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-[#1e3a50]">{proj.latest_update.title}</p>
                  <p className="text-[11px] font-medium text-[#6b8a9e] line-clamp-2">{proj.latest_update.summary}</p>
                  <p className="text-[9px] text-[#8faab8] mt-1">{new Date(proj.latest_update.created_at).toLocaleDateString()}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-300" />
              </CardContent>
            </Card>
          )}

          {/* Outstanding Balance */}
          {proj.outstanding_balance > 0 && (
            <Card className="border-amber-200 bg-amber-50 shadow-sm">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-amber-800">Outstanding Balance</p>
                  <p className="text-2xl font-extrabold font-mono text-amber-900">${proj.outstanding_balance.toLocaleString()}</p>
                </div>
                <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => navigate('/portal/billing')}>
                  <CreditCard className="w-4 h-4 mr-1" /> View Billing
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Quick Actions */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Progress', icon: Construction, to: '/portal/progress', color: 'bg-[#4a9ac5]/10 text-[#2a6496]' },
              { label: 'Photos', icon: Camera, to: '/portal/photos', color: 'bg-emerald-50 text-emerald-700' },
              { label: 'Documents', icon: FileText, to: '/portal/documents', color: 'bg-violet-50 text-violet-700' },
              { label: 'Message HDH', icon: MessageSquare, to: '/portal/support', color: 'bg-amber-50 text-amber-700' },
            ].map(a => (
              <button key={a.label} onClick={() => navigate(a.to)}
                className={`flex flex-col items-center gap-2 p-5 rounded-xl font-bold text-xs transition-all hover:shadow-md bg-white border border-transparent hover:border-[#c8dce8]`}>
                <div className={`w-10 h-10 rounded-lg ${a.color} flex items-center justify-center`}><a.icon className="w-5 h-5" /></div>
                {a.label}
              </button>
            ))}
          </div>
        </>
      ) : (
        <Card className="border-0 shadow-sm"><CardContent className="p-8 text-center">
          <p className="text-sm font-medium text-[#6b8a9e]">No active projects yet. Your project will appear here once it's been set up.</p>
        </CardContent></Card>
      )}
    </div>
  );
}
